import type { Permission } from '@shared/infrastructure/auth/permission.js';
import type { AnalysisQueue } from '../model.js';
import { formatCount } from './format-count.js';

/**
 * Virtual Profiler's own navigation: which screens it has, and which of them you are on.
 *
 * The sidebar lists modules, not screens, so this is the only place that knows the module's
 * sections. It is data rather than markup so that the route walk in the spec can hold it against
 * the real router: a route added to Virtual Profiler without a section here would leave its
 * screen with no tab lit, and nothing on screen would say so.
 *
 * `members` is what makes a detail screen belong somewhere. The routes are flat — a nested layout
 * route would break `meta.slidesIn` — so «the video detail is under Dataset» cannot be read off the
 * router and has to be said here, once.
 */

export type VpSectionKey = 'overview' | 'queue' | 'videos' | 'groups' | 'configs';

export interface VpSection {
  key: VpSectionKey;
  /** The visible tab text. Below `xl` only the active tab keeps it. */
  label: string;
  /** The tooltip and the start of the accessible name. Always starts with `label`. */
  title: string;
  /** The screen the tab opens. */
  routeName: string;
  /** Every route that belongs to the section, `routeName` first. */
  members: readonly string[];
}

function section(entry: VpSection): VpSection {
  return Object.freeze({
    ...entry,
    members: Object.freeze([...entry.members]),
  });
}

/**
 * In the product owner's order: the summary, then the experiments, the corpus they are made of,
 * the work in progress, and the engines' settings last. Not the context's module order, which is
 * about registration and says nothing about how the module is read.
 */
export const VP_SECTIONS: readonly VpSection[] = Object.freeze([
  section({
    key: 'overview',
    label: 'Resumen',
    title: 'Resumen de Virtual Profiler',
    routeName: 'virtual-profiler',
    members: ['virtual-profiler'],
  }),
  section({
    key: 'groups',
    // «Control» since the control groups (product owner, 2026-09-28): the screen holds the three
    // control groups above the analysis groups. The key and the route keep their names.
    label: 'Control',
    title: 'Control',
    routeName: 'virtual-profiler-analysis-groups',
    // The engines' changelog is opened from the board's Motores card, so it is read under Control.
    members: [
      'virtual-profiler-analysis-groups',
      'virtual-profiler-group-detail',
      'virtual-profiler-control-detail',
      'virtual-profiler-execution-detail',
      'virtual-profiler-control-execution-detail',
      'virtual-profiler-engine-changelog',
    ],
  }),
  section({
    key: 'videos',
    // «Data» since the persons joined it (product owner, 2026-10-14): the people, each a database of
    // videos, CVs and contexts, and every video with its groups. «Dataset» before, since the groups
    // became subsets of it (2026-10-05). The key and the route keep their names.
    label: 'Data',
    title: 'Data',
    routeName: 'virtual-profiler-videos',
    members: [
      'virtual-profiler-videos',
      'virtual-profiler-persons',
      'virtual-profiler-person',
      'virtual-profiler-video-detail',
    ],
  }),
  section({
    key: 'queue',
    label: 'Cola',
    title: 'Cola de análisis',
    routeName: 'virtual-profiler-queue',
    members: ['virtual-profiler-queue'],
  }),
  section({
    key: 'configs',
    label: 'Configuración',
    title: 'Configuración de análisis',
    // Opens the active configuration: the route finds it and hands over to its detail.
    routeName: 'virtual-profiler-analysis-config',
    members: [
      'virtual-profiler-analysis-config',
      'virtual-profiler-config-list',
      'virtual-profiler-config-new',
      'virtual-profiler-config-edit',
      'virtual-profiler-config-versions',
      'virtual-profiler-gesture-catalogue',
    ],
  }),
]);

/** The section a route belongs to. `unknown` because vue-router names can be symbols, or absent. */
export function vpSectionOf(routeName: unknown): VpSectionKey | null {
  if (typeof routeName !== 'string') return null;
  return VP_SECTIONS.find((entry) => entry.members.includes(routeName))?.key ?? null;
}

/**
 * The sections this session can open.
 *
 * Same rule as the sidebar's, for the same reason: the permission is read from the route, so the
 * tab and the guard cannot disagree — a tab that answers with a 403 is a promise the screen cannot
 * keep. A section whose route is missing is dropped rather than rendered as a link to nowhere.
 */
export function visibleVpSections(
  sections: readonly VpSection[],
  lookup: (name: string) => { permission?: Permission } | undefined,
  can: (permission: Permission) => boolean,
): VpSection[] {
  return sections.filter((entry) => {
    const target = lookup(entry.routeName);

    if (target === undefined) return false;

    return target.permission === undefined || can(target.permission);
  });
}

/**
 * `page` on the screen the tab opens, `true` on the screens under it.
 *
 * The distinction is what a screen reader announces: on the list, the link *is* this page; on a
 * video's detail, the Dataset tab is where you are but following it goes somewhere else. Claiming
 * `page` there would promise a no-op link that in fact navigates.
 */
export function ariaCurrentFor(
  section: VpSection,
  routeName: unknown,
): 'page' | 'true' | undefined {
  if (typeof routeName !== 'string') return undefined;
  if (routeName === section.routeName) return 'page';
  return section.members.includes(routeName) ? 'true' : undefined;
}

/**
 * How old a cached queue may be and still be shown on the Cola tab, on a screen that does not
 * read the queue itself.
 *
 * The nav reads the queue live on every Virtual Profiler screen (`live`), so there this window
 * never applies. It stays for a caller that only has a cached read: two minutes, so a count nobody
 * is refreshing does not sit there for the rest of the session looking current.
 */
export const NAV_SIGNAL_FRESH_MS = 120_000;

/** What the cache knows about its own queue read, beyond the answer and its age. */
export interface QueueCacheState {
  /**
   * A screen below the tab reads the queue itself (the hub, the queue screen).
   *
   * There the clock does not apply: an idle queue — paused, or with lost runs and nothing moving —
   * is not polled, so its read can be minutes old and still be what the page under the tab shows.
   * Dropping the tab's dot there would contradict the page on the same screen.
   */
  live?: boolean;
  /** The last read failed. The answer still cached describes a moment that is gone. */
  failed?: boolean;
  /** Somebody said the answer is out of date and nothing has read it again. */
  invalidated?: boolean;
}

export interface QueueTabSignal {
  /**
   * Runs waiting in the queue — what «la cola» means to whoever reads the tab, and the «En espera»
   * figure of the queue screen. What is running is the dot. Null at zero: a «0» pill is noise.
   */
  count: number | null;
  dot: 'danger' | 'warning' | 'info' | null;
  /** The same state in words, because a dot's colour is never the only channel. */
  description: string;
}

type QueueSnapshot = Pick<AnalysisQueue, 'running' | 'waiting' | 'paused' | 'slots' | 'health'>;

/**
 * What the Cola tab says about the queue, or `null` when it should say nothing.
 *
 * A cache that is missing, stale, failed or invalidated is **absent**, never an empty queue:
 * showing no count because the cache is old would read as «nothing running», which is a claim the
 * screen cannot make. On a live screen only a failed read silences it — an invalidation there is
 * already being refetched, and the age is the page's business.
 *
 * `now` may trail `updatedAt` — the clock ticks every few seconds and a fresh read can land in
 * between — and a read from the future is simply fresh.
 */
export function queueTabSignal(
  queue: QueueSnapshot | undefined,
  updatedAt: number,
  now: number,
  cache: QueueCacheState = {},
): QueueTabSignal | null {
  if (queue === undefined || updatedAt <= 0 || cache.failed) return null;
  if (!cache.live && (cache.invalidated || now - updatedAt > NAV_SIGNAL_FRESH_MS)) return null;

  const running = queue.running.length;
  const waiting = queue.waiting.length;
  const lost = queue.health.lostRuns;
  // Blind on its own is the ordinary local state (no worker view, nothing to dispatch). Blind
  // with work waiting is work stuck, which is QueueHealthStrip's first alarm too.
  const blindWithWork = queue.slots.source === 'unknown' && waiting > 0;

  const dot: QueueTabSignal['dot'] =
    lost > 0 || !queue.health.bridgeAlive
      ? 'danger'
      : queue.paused || blindWithWork
        ? 'warning'
        : running > 0
          ? 'info'
          : null;

  const count = waiting > 0 ? waiting : null;

  if (count === null && dot === null) return null;

  const parts: string[] = [];
  if (running > 0) parts.push(`${formatCount(running)} en marcha`);
  if (waiting > 0) parts.push(`${formatCount(waiting)} en espera`);
  if (queue.paused) parts.push('en pausa');
  if (blindWithWork) parts.push('sin workers disponibles');
  if (lost > 0) parts.push(lost === 1 ? '1 perdido' : `${formatCount(lost)} perdidos`);
  if (!queue.health.bridgeAlive) parts.push('sin conexión con el worker');

  return { count, dot, description: parts.join(' · ') };
}

/**
 * The tab's accessible name, which is also its tooltip.
 *
 * It starts with the visible label so that voice control («pulsa Cola») still finds the link, and
 * it carries the signal in words, since the pill and the dot are only digits and colour.
 */
export function sectionAccessibleName(section: VpSection, signal: QueueTabSignal | null): string {
  return signal && signal.description ? `${section.title} · ${signal.description}` : section.title;
}
